// ═══════════════════════════════════════════════════════════════════
//  ONE DAY OF A STAFF MEMBER'S WEEK
//
//  The staff week can be narrowed to a single day (AB#766). The day is
//  one column of the week already loaded — the same endpoint, the same
//  cache entry — so switching between the two never asks the server for
//  anything. Only stepping past Friday or before Monday lands in another
//  week, and that is the week view's own fetch.
//
//  The grid draws Monday to Friday only, so a day view on a Saturday or
//  Sunday would have no column to draw. The day in view is therefore a
//  weekday by construction: a weekend date (today, on a Saturday) shows
//  that week's Friday, and stepping skips the weekend in the direction
//  of travel.
// ═══════════════════════════════════════════════════════════════════

import { addDays, mondayOf, weekdayIndexOf } from "./siteTime"
import type { CalendarDate, WeekDate } from "./siteTime"

/** Which of the two the staff calendar is drawing. */
export type StaffCalendarView = "week" | "day"

/** Monday = 0 … Friday = 4: the days the grid has columns for. */
const LAST_WEEKDAY = 4

/** The weekday a date is shown as: itself, or that week's Friday for a Saturday or Sunday. */
export function weekdayInView(date: CalendarDate): CalendarDate {
  return weekdayIndexOf(date) > LAST_WEEKDAY ? addDays(mondayOf(date), LAST_WEEKDAY) : date
}

/**
 * The weekday `delta` working days from the one in view. Friday forward is Monday; Monday back is
 * Friday. Stepped one day at a time so a delta of more than one still skips every weekend it crosses.
 */
export function stepWeekday(date: CalendarDate, delta: number): CalendarDate {
  let current = weekdayInView(date)
  const direction = Math.sign(delta)

  for (let remaining = Math.abs(delta); remaining > 0; remaining--) {
    do {
      current = addDays(current, direction)
    } while (weekdayIndexOf(current) > LAST_WEEKDAY)
  }

  return current
}

/**
 * The columns to draw: the whole week, or the one day in view out of it. Cut from the week the page
 * already holds rather than built afresh, so the column is the same object the week view drew.
 */
export function columnsInView(
  weekDates: readonly WeekDate[],
  view: StaffCalendarView,
  selectedDate: CalendarDate,
): WeekDate[] {
  if (view === "week") return [...weekDates]
  const shown = weekdayInView(selectedDate)
  return weekDates.filter(day => day.isoDate === shown)
}
